// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { StatusBanner } from './StatusBanner';

export interface Toast {
  id: string;
  message: ReactNode;
  variant: 'success' | 'error' | 'neutral';
  visible: boolean;
}

interface ToastContextType {
  toast: Toast | null;
  showToast: (message: ReactNode, variant?: Toast['variant'], duration?: number) => void;
  dismissToast: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const DEFAULT_AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismissToast = useCallback(() => {
    setToast(prev => prev ? { ...prev, visible: false } : null);
    // Remove from DOM after exit animation
    setTimeout(() => setToast(null), 300);
  }, []);

  const showToast = useCallback((
    message: ReactNode,
    variant: Toast['variant'] = 'success',
    duration = DEFAULT_AUTO_DISMISS_MS,
  ) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const id = `toast-${Date.now()}`;
    setToast({ id, message, variant, visible: true });

    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        dismissToast();
      }, duration);
    }
  }, [dismissToast]);

  return (
    <ToastContext.Provider value={{ toast, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

/**
 * ToastOutlet renders the current toast.
 * Place this inside the main content area so the toast appears
 * above the content panel rather than spanning the full page.
 */
export function ToastOutlet() {
  const { toast, dismissToast } = useToast();

  if (!toast) return null;

  return (
    <div className="fin-toast-outlet">
      <StatusBanner
        variant={toast.variant}
        message={toast.message}
        visible={toast.visible}
        onDismiss={dismissToast}
        position="fixed"
      />
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Provider-tolerant variant: returns null instead of throwing when no
 * ToastProvider is mounted. For components that treat the toast as a
 * nice-to-have confirmation rather than a requirement (and for isolated
 * unit tests that render without the provider).
 */
export function useOptionalToast(): ToastContextType | null {
  return useContext(ToastContext);
}
